/* ==========================================================================
   health-quiz.js — sections/health-quiz.liquid
   ========================================================================== */

(() => {
  'use strict';

  const root = document.querySelector('[data-hq-root]');
  if (!root) return;

  /** @type {{data: any, text: Record<string, any>, klaviyo: {key: string, list: string, metric: string, source: string}, currency: string, locale: string, page: string}} */
  let cfg;
  try {
    cfg = JSON.parse(root.querySelector('[data-hq-json]').textContent);
  } catch (error) {
    console.warn('[health-quiz] data block unreadable', error);
    return;
  }

  const T = cfg.text;
  const D = cfg.data;
  const LETTERS = 'ABCDEFGH';
  const shopRoot = window.Shopify?.routes?.root || '/';
  const landing = root.querySelector('[data-hq-landing]');
  const results = root.querySelector('[data-hq-results]');
  const dialog = root.querySelector('[data-hq-dialog]');
  const toastEl = root.querySelector('[data-hq-toast]');

  /* ---------------------------------------------------------------- helpers */
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const fill = (tpl, vars) => String(tpl || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? ''));
  // The section builds the locale from language + country; fall back if the browser rejects that pair.
  const LOCALE = (() => { try { return Intl.getCanonicalLocales(cfg.locale)[0]; } catch (error) { return 'en-CA'; } })();
  const moneyFmt = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: cfg.currency || 'CAD' });
  const money = (cents) => moneyFmt.format(cents / 100);
  const joinAnd = (a) => (a.length < 2 ? a.join('') : `${a.slice(0, -1).join(', ')} ${T.and} ${a[a.length - 1]}`);
  // Icons come from the inline sprite in the section (<symbol id="hq-i-NAME">).
  const icon = (name) => `<svg class="hq-i" aria-hidden="true" focusable="false"><use href="#hq-i-${esc(name)}"/></svg>`;
  const dot = (name, mod = '') => `<span class="hq-dot ${mod}">${icon(name)}</span>`;
  const plate = (src, alt) => `<span class="hq-plate">${src ? `<img src="${esc(src)}" alt="${esc(alt)}" width="160" height="160" loading="lazy" decoding="async">` : ''}</span>`;

  function track(name, params = {}) {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      else (window.dataLayer = window.dataLayer || []).push({ event: name, ...params });
    } catch (error) { /* analytics must never break the quiz */ }
  }

  /* ------------------------------------------------- areas, products, questions */
  const byArea = new Map(D.areas.map((a) => [a.key, []]));
  for (const product of D.products) {
    for (const key of product.areas) byArea.get(key)?.push(product);
  }
  // An area with no in-stock product is never asked about (brand quizzes cover fewer areas).
  const AREAS = D.areas.filter((a) => byArea.get(a.key).length);
  const AREA = Object.fromEntries(AREAS.map((a) => [a.key, a]));
  const ORDER = AREAS.map((a) => a.key);

  const QS = [
    ...AREAS.map((a) => ({
      id: a.key,
      area: a.key,
      text: a.question,
      options: a.reverse
        ? [[a.answers[2], 'focus'], [a.answers[1], 'watch'], [a.answers[0], 'none']]
        : [[a.answers[0], 'strength'], [a.answers[1], 'watch'], [a.answers[2], 'focus']]
    })),
    {
      id: 'priority',
      text: T.q_priority,
      help: T.q_priority_help,
      noSkip: true,
      options: [...AREAS.map((a) => [a.label, a.key]), [T.priority_none, 'none']]
    },
    { id: 'supplements', text: T.q_supp, help: T.q_supp_help, options: [[T.yes, 'yes'], [T.no, 'no']] }
  ];
  const TOTAL = QS.length + 1;

  function pick(key) {
    const list = byArea.get(key) || [];
    const bundle = list.find((p) => p.components);
    return {
      hero: list.find((p) => !p.components),
      bundle,
      more: list.filter((p) => p.components && p !== bundle).slice(0, 3)
    };
  }

  /* ----------------------------------------------------------------- state */
  const KEY = `hq:${D.quiz}`;
  const fresh = () => ({ step: 0, answers: {}, name: '', email: '', consent: false, emailed: false, completedAt: '' });
  let state = fresh();
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (saved && saved.answers) state = saved;
  } catch (error) { /* private mode: start fresh */ }
  const save = () => { try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (error) { /* ignore */ } };

  /* --------------------------------------------------------------- landing */
  function updateCtas() {
    const inProgress = state.step > 0 && state.step < TOTAL;
    for (const el of root.querySelectorAll('[data-hq-start] [data-hq-label]')) el.textContent = inProgress ? T.resume : T.start;
    const note = root.querySelector('[data-hq-resume]');
    const sub = root.querySelector('[data-hq-cta-sub]');
    if (!note) return;
    note.hidden = !inProgress;
    if (sub) sub.hidden = inProgress;
    if (inProgress) note.innerHTML = `${esc(fill(T.resume_note, { n: state.step + 1, total: TOTAL }))} <button class="hq-link" type="button" data-hq-restart>${esc(T.start_over)}</button>`;
  }

  /* ------------------------------------------------------------- quiz dialog */
  let built = false;
  function buildDialog() {
    if (built) return;
    built = true;
    dialog.innerHTML = `
      <div class="hq-qbar">
        <div class="hq-qbar__row">
          <button class="hq-iconbtn" type="button" data-hq-back aria-label="${esc(T.prev)}">${icon('back')}</button>
          <div class="hq-qbar__mid"><b>${esc(T.q_title)}</b><span class="hq-count" data-hq-count aria-live="polite"></span></div>
          <button class="hq-iconbtn" type="button" data-hq-close aria-label="${esc(T.close)}">${icon('close')}</button>
        </div>
        <div class="hq-progress" role="progressbar" aria-label="${esc(T.progress)}" aria-valuemin="0" aria-valuemax="${TOTAL}" aria-valuenow="0"><i></i></div>
      </div>
      <div class="hq-stage" data-hq-stage></div>
      <div class="hq-qfoot"><span>${icon('lock')}${esc(T.foot_private)}</span><span>${icon('shield')}${esc(T.foot_reviewed)}</span></div>`;
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeQuiz(); });
  }

  function openQuiz(restart) {
    if (restart) state = fresh();
    if (state.step >= TOTAL) state.step = 0;
    buildDialog();
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    if (state.step === 0 && !Object.keys(state.answers).length) track('hq_start', { quiz: D.quiz });
    renderStep(false);
  }

  // <dialog> methods are missing before Safari 15.4: fall back to the open attribute.
  function hideDialog() {
    if (typeof dialog.close === 'function') { if (dialog.open) dialog.close(); } else dialog.removeAttribute('open');
  }

  function closeQuiz() {
    hideDialog();
    save();
    updateCtas();
  }

  // ~12 s per screen, rounded up to whole minutes: honest and reassuring.
  function timeLeft(i) {
    const seconds = (TOTAL - i) * 12;
    return seconds <= 60 ? T.time_last : fill(T.time_left, { min: Math.ceil(seconds / 60) });
  }

  function renderStep(back) {
    const i = state.step;
    dialog.querySelector('[data-hq-back]').disabled = i === 0;
    dialog.querySelector('[data-hq-count]').textContent = `${fill(T.count, { n: i + 1, total: TOTAL })} · ${timeLeft(i)}`;
    dialog.querySelector('.hq-progress i').style.width = `${(i / TOTAL) * 100}%`;
    dialog.querySelector('.hq-progress').setAttribute('aria-valuenow', String(i));
    const stage = dialog.querySelector('[data-hq-stage]');
    stage.innerHTML = i < QS.length ? questionHTML(QS[i]) : captureHTML();
    if (back) stage.firstElementChild.classList.add('hq-screen--back');
    dialog.scrollTop = 0;
    const first = stage.querySelector('[aria-pressed="true"]') || stage.querySelector('input, .hq-opt');
    first?.focus({ preventScroll: true });
  }

  function questionHTML(q) {
    const current = state.answers[q.id];
    const area = q.area ? AREA[q.area] : null;
    const label = area
      ? `${dot(area.icon)}<span class="hq-eyebrow hq-eyebrow--plain">${esc(fill(T.area_of, { n: ORDER.indexOf(q.area) + 1, total: ORDER.length }))} · ${esc(area.label)}</span>`
      : `${dot(q.id === 'priority' ? 'sprout' : 'list')}<span class="hq-eyebrow hq-eyebrow--plain">${esc(q.id === 'priority' ? T.your_priority : T.about_you)}</span>`;
    const options = q.options.map(([text, value], k) => {
      const lead = q.id === 'priority' && AREA[value]
        ? `<span class="hq-opt__ic">${icon(AREA[value].icon)}</span>`
        : '<span class="hq-opt__radio" aria-hidden="true"></span>';
      return `<button type="button" class="hq-opt" data-hq-val="${esc(value)}" aria-pressed="${current === value}" aria-keyshortcuts="${LETTERS[k]}">${lead}<span>${esc(text)}</span><kbd aria-hidden="true">${LETTERS[k]}</kbd></button>`;
    }).join('');
    const skip = q.noSkip ? '' : `<button type="button" class="hq-link hq-skip" data-hq-val="skip">${esc(T.skip)}</button>`;
    return `<div class="hq-screen">
      <div class="hq-q-label">${label}</div>
      <h2 class="hq-q-title" id="hq-q-title">${esc(q.text)}</h2>
      ${q.help ? `<p class="hq-q-help">${esc(q.help)}</p>` : ''}
      <div class="hq-opts${q.options.length > 5 ? ' hq-opts--grid' : ''}" role="group" aria-labelledby="hq-q-title">${options}</div>
      ${skip}</div>`;
  }

  function captureHTML() {
    return `<form class="hq-screen" data-hq-capture novalidate>
      <div class="hq-q-label">${dot('check')}<span class="hq-eyebrow hq-eyebrow--plain">${esc(T.almost_done)}</span></div>
      <h2 class="hq-q-title" id="hq-q-title">${esc(T.cap_title)}</h2>
      <p class="hq-q-help">${esc(T.cap_text)}</p>
      <div class="hq-field"><label for="hq-name">${esc(T.cap_name)}</label><input id="hq-name" name="first_name" autocomplete="given-name" value="${esc(state.name)}"></div>
      <div class="hq-field"><label for="hq-email">${esc(T.cap_email)}</label><input id="hq-email" name="email" type="email" inputmode="email" autocomplete="email" value="${esc(state.email)}"></div>
      <label class="hq-check" for="hq-consent"><input id="hq-consent" type="checkbox"${state.consent ? ' checked' : ''}><span>${esc(T.cap_consent)}</span></label>
      <p class="hq-err" data-hq-err role="alert" hidden></p>
      <button class="hq-btn hq-btn--block" type="submit">${esc(T.cap_submit)}${icon('arrow')}</button>
      <div class="hq-capture__alt"><button class="hq-link" type="button" data-hq-no-email>${esc(T.cap_skip)}</button></div></form>`;
  }

  // One answer per screen: a second tap or key press during the hand-off delay must not skip a question.
  let advancing = false;
  function choose(value, button) {
    if (advancing) return;
    advancing = true;
    const q = QS[state.step];
    state.answers[q.id] = value;
    if (button) {
      for (const b of button.parentElement.querySelectorAll('.hq-opt')) b.setAttribute('aria-pressed', String(b === button));
    }
    track('hq_step', { quiz: D.quiz, step: state.step + 1, question: q.id, answer: value });
    save();
    setTimeout(() => { advancing = false; state.step += 1; save(); renderStep(false); }, button ? 360 : 120);
  }

  function submitCapture(withEmail) {
    const form = dialog.querySelector('[data-hq-capture]');
    const name = form.querySelector('#hq-name').value.trim();
    const email = form.querySelector('#hq-email').value.trim();
    const consent = form.querySelector('#hq-consent').checked;
    if (withEmail) {
      const okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
      form.querySelector('#hq-email').setAttribute('aria-invalid', String(!okEmail));
      form.querySelector('#hq-name').setAttribute('aria-invalid', String(!name));
      if (!okEmail || !name) {
        const err = form.querySelector('[data-hq-err]');
        err.textContent = okEmail ? T.err_name : T.err_email;
        err.hidden = false;
        form.querySelector(okEmail ? '#hq-name' : '#hq-email').focus();
        return;
      }
    }
    Object.assign(state, {
      name: withEmail ? name : '', email: withEmail ? email : '', consent: withEmail && consent,
      emailed: withEmail, step: TOTAL, completedAt: new Date().toISOString()
    });
    save();
    hideDialog();
    const r = compute(state.answers);
    track('hq_complete', { quiz: D.quiz, result: r.main || 'browse', emailed: withEmail, consent: state.consent });
    if (withEmail) sendToKlaviyo(r);
    showResults(r.main);
  }

  /* ----------------------------------------------------------- result logic */
  function compute(answers, forceMain) {
    const status = {};
    for (const key of ORDER) {
      const v = answers[key];
      status[key] = !v || v === 'skip' ? 'skip' : v;
    }
    const focus = ORDER.filter((k) => status[k] === 'focus');
    const strengths = ORDER.filter((k) => status[k] === 'strength' && AREA[k].strength);
    const priority = answers.priority;
    const main = forceMain || (priority && priority !== 'none' && AREA[priority] ? priority : focus[0] || null);
    return { status, focus, strengths, main, also: focus.filter((k) => k !== main), supplements: answers.supplements || 'skip' };
  }

  function summary(r) {
    const out = [];
    if (r.strengths.length) {
      const list = `<b>${esc(joinAnd(r.strengths.map((k) => AREA[k].strength)))}</b>`;
      out.push(fill(r.strengths.length > 1 ? T.s_strength_many : T.s_strength_one, { list }));
    }
    if (r.main) {
      const wants = [r.main, ...r.also];
      out.push(esc(fill(T.s_want, { list: joinAnd(wants.map((k) => AREA[k].want)) })));
      const list = `<b>${esc(joinAnd(wants.map((k) => AREA[k].area)))}</b>`;
      out.push(fill(wants.length > 1 ? T.s_focus_many : T.s_focus_one, { list }));
    }
    return out.join(' ');
  }

  /* --------------------------------------------------------------- results */
  function arc(i, n, r1, r2, gap) {
    const step = 360 / n;
    const a0 = ((-90 + i * step + gap / 2) * Math.PI) / 180;
    const a1 = ((-90 + (i + 1) * step - gap / 2) * Math.PI) / 180;
    const p = (r, a) => `${(r * Math.cos(a)).toFixed(2)} ${(r * Math.sin(a)).toFixed(2)}`;
    return `M${p(r2, a0)} A${r2} ${r2} 0 0 1 ${p(r2, a1)} L${p(r1, a1)} A${r1} ${r1} 0 0 0 ${p(r1, a0)}Z`;
  }

  function ringHTML(r) {
    const center = r.main ? [T.r_priority, AREA[r.main].label.split(' ')[0]] : [T.r_six, T.balanced];
    const segs = ORDER.map((k, i) => `<path class="hq-seg--${r.status[k]}" d="${arc(i, ORDER.length, 64, k === r.main ? 106 : 96, 3)}"><title>${esc(AREA[k].label)}: ${esc(T.status[r.status[k]])}</title></path>`).join('');
    return `<svg class="hq-ring" viewBox="-110 -110 220 220" role="img" aria-label="${esc(T.r_six)}">${segs}
      <text class="hq-ring__sub" text-anchor="middle" y="-10">${esc(center[0])}</text><text class="hq-ring__label" text-anchor="middle" y="14">${esc(center[1])}</text></svg>`;
  }

  function sixHTML(r) {
    const rows = ORDER.map((k) => `<li>${dot(AREA[k].icon)}<span>${esc(AREA[k].label)}${k === r.main ? ` · ${esc(T.priority_tag)}` : ''}</span><span class="hq-st hq-st--${r.status[k]}">${esc(T.status[r.status[k]])}</span></li>`).join('');
    return `<section class="hq-rsec"><div class="hq-rsec__h"><h2 class="hq-h2" style="font-size:clamp(24px,3vw,32px)">${esc(T.r_six)}</h2><span class="hq-muted" style="font-size:13.5px">${esc(T.r_six_note)}</span></div><ul class="hq-legend">${rows}</ul></section>`;
  }

  function npnLine(npn) {
    return npn ? `<span class="hq-npn">NPN ${esc(npn)}</span>` : '';
  }

  function heroHTML(p) {
    return `<article class="hq-card"><div class="hq-card__top"><span class="hq-eyebrow hq-eyebrow--plain">${esc(T.r_hero)}</span></div>
      <div class="hq-hero-prod">${plate(p.image, p.title)}<div><span class="hq-brand">${esc(p.brand)}</span><a class="hq-pname" href="${esc(p.url)}">${esc(p.title)}</a><span class="hq-pfmt">${esc(p.format)}</span>${npnLine(p.npn)}</div></div>
      ${p.pitch ? `<p class="hq-why">${esc(p.pitch)}</p>` : ''}
      <span class="hq-price">${money(p.price)}</span>
      <button class="hq-btn hq-btn--ghost hq-btn--block" type="button" data-hq-add="${p.variant}" data-hq-item="${esc(p.title)}">${esc(T.r_add)}</button>
      <p class="hq-atc-err" data-hq-atc-err hidden></p></article>`;
  }

  function bundleHTML(p) {
    const saving = Math.max(0, p.total - p.price);
    const pct = p.total ? Math.round((saving / p.total) * 100) : 0;
    const comps = p.components.map((c) => `<li>${plate(c.image, c.title)}<div><b>${c.qty > 1 ? `${c.qty} × ` : ''}${esc(c.title)}</b><span>${esc([c.brand, c.format].filter(Boolean).join(' · '))}</span>${npnLine(c.npn)}</div><span class="hq-comps__p">${money(c.price)}</span></li>`).join('');
    return `<article class="hq-card hq-card--bundle"><span class="hq-card__tab">${esc(T.r_recommended)}</span>
      <div class="hq-card__top"><span class="hq-eyebrow hq-eyebrow--plain">${esc(T.r_bundle)}</span>${pct > 0 ? `<span class="hq-pill">${esc(fill(T.r_save, { pct }))}</span>` : ''}</div>
      <div><h3><a href="${esc(p.url)}">${esc(p.title)}</a></h3><p class="hq-stack">${p.stack ? `“${esc(p.stack)}” · ` : ''}${esc(fill(T.r_products, { n: p.components.length }))}</p></div>
      ${p.pitch ? `<p class="hq-why">${esc(p.pitch)}</p>` : ''}
      <ul class="hq-comps">${comps}</ul>
      <div class="hq-math">${saving > 0 ? `<div class="hq-math__row"><span>${esc(T.r_separately)}</span><span>${money(p.total)}</span></div>` : ''}
        <div class="hq-math__row hq-math__row--total"><span>${esc(T.r_bundle_price)}</span><span>${money(p.price)}</span></div>
        ${saving > 0 ? `<div class="hq-math__row hq-math__row--keep"><span>${esc(T.r_keep)}</span><span>${money(saving)}</span></div>` : ''}</div>
      <button class="hq-btn hq-btn--block" type="button" data-hq-add="${p.variant}" data-hq-item="${esc(p.title)}">${esc(T.r_add_bundle)}${icon('arrow')}</button>
      <p class="hq-atc-err" data-hq-atc-err hidden></p></article>`;
  }

  function moreHTML(list, area) {
    if (!list.length) return '';
    const rows = list.map((p) => `<a href="${esc(p.url)}">${plate(p.image, p.title)}<div><b>${esc(p.title)}</b><span>${esc(fill(T.r_products, { n: p.components.length }))}</span></div><strong>${money(p.price)} →</strong></a>`).join('');
    return `<section class="hq-rsec"><h2 class="hq-h3">${esc(fill(T.r_more, { area: area.label }))}</h2><div class="hq-more">${rows}</div></section>`;
  }

  function footHTML() {
    return `<section class="hq-rsec">
      ${state.emailed ? `<p class="hq-note">${esc(fill(T.r_emailed, { email: state.email }))}</p>` : ''}
      <div class="hq-know" role="note" style="margin:0">${icon('info')}<div><b>${esc(T.r_know_title)}</b>${esc(T.disclaimer)}</div></div>
      <div class="hq-actions"><button class="hq-btn hq-btn--ghost" type="button" data-hq-retake>${esc(T.r_retake)}</button>
      <button class="hq-btn hq-btn--ghost" type="button" data-hq-home>${esc(T.r_back)}</button></div></section>`;
  }

  function headHTML(r, title, area) {
    const date = new Date(state.completedAt || Date.now()).toLocaleDateString(LOCALE, { month: 'long', day: 'numeric', year: 'numeric' });
    const meta = state.name ? fill(T.r_meta, { name: esc(state.name), date: esc(date) }) : fill(T.r_meta_anon, { date: esc(date) });
    return `<header class="hq-rhead"><div class="hq-shell"><div class="hq-rhead__copy">
        <span class="hq-eyebrow">${esc(T.r_eyebrow)}</span>
        <p class="hq-rhead__meta">${meta}</p>
        <h1 class="hq-display" tabindex="-1">${area ? dot(area.icon, 'hq-dot--lg') : ''}${esc(title)}</h1>
        <p class="hq-sum">${r.main ? summary(r) : `${summary(r)} ${esc(T.r_browse_text)}`}</p></div>
      ${ringHTML(r)}</div></header>`;
  }

  function showResults(forceMain) {
    const r = compute(state.answers, forceMain);
    let body;
    if (!r.main) {
      body = `${sixHTML(r)}
        <section class="hq-rsec"><h2 class="hq-h3">${esc(fill(T.r_browse_heading, { n: ORDER.length }))}</h2><div class="hq-browse">${AREAS.map((a) => `<button type="button" data-hq-goto="${esc(a.key)}">${dot(a.icon)}<div><b>${esc(a.label)}</b><span>${esc(a.want ? fill(T.r_browse_card, { want: a.want }) : '')}</span></div></button>`).join('')}</div></section>
        ${footHTML()}`;
      body = `${headHTML(r, T.r_browse_title, null)}<div class="hq-shell hq-rbody">${body}</div>`;
    } else {
      const area = AREA[r.main];
      const { hero, bundle, more } = pick(r.main);
      const bundleFirst = r.supplements !== 'no';
      const cards = [hero && heroHTML(hero), bundle && bundleHTML(bundle)].filter(Boolean);
      if (bundleFirst) cards.reverse();
      const habits = (area.habits || []).map((h) => {
        const [title, detail] = String(h).split('|').map((s) => s.trim());
        return `<li><div><b>${esc(title)}</b>${detail ? `<span>${esc(detail)}</span>` : ''}</div></li>`;
      }).join('');
      body = `${headHTML(r, area.label, area)}<div class="hq-shell hq-rbody">
        ${sixHTML(r)}
        <section class="hq-rsec"><div class="hq-two">
          <div>${area.season ? `<h2 class="hq-h3">${esc(T.r_season)}</h2><p>${esc(area.season)}</p>` : ''}
            ${T.r_team_note ? `<div class="hq-note-label"><b>${esc(T.r_team_title)}</b><p>${esc(T.r_team_note)}</p></div>` : ''}</div>
          ${habits ? `<div><h2 class="hq-h3">${esc(T.r_habits)}</h2><ol class="hq-habits">${habits}</ol></div>` : ''}
        </div></section>
        <section class="hq-rsec"><div class="hq-rsec__h"><h2 class="hq-h2" style="font-size:clamp(24px,3vw,32px)">${esc(T.r_routine)}</h2><span class="hq-eyebrow hq-eyebrow--plain">${esc(area.label)}</span></div>
          <p class="hq-muted" style="max-width:60ch">${esc(T.r_routine_text)}</p>
          <div class="hq-offers${bundleFirst && cards.length > 1 ? ' hq-offers--bundle-first' : ''}" style="margin-block-start:12px">${cards.join('')}</div>
          <p class="hq-note">${esc(r.supplements === 'no' ? T.r_supp_no : T.r_supp_yes)}</p></section>
        ${moreHTML(more, area)}
        ${r.also.length ? `<section class="hq-rsec"><h2 class="hq-h3">${esc(T.r_also)}</h2><div class="hq-chips">${r.also.map((k) => `<button class="hq-chip" type="button" data-hq-goto="${esc(k)}">${dot(AREA[k].icon)}${esc(fill(T.r_also_chip, { area: AREA[k].label }))}</button>`).join('')}</div></section>` : ''}
        ${footHTML()}</div>`;
    }
    results.innerHTML = body;
    landing.hidden = true;
    results.hidden = false;
    const url = new URL(window.location.href);
    url.searchParams.set('result', r.main || 'browse');
    history.replaceState(null, '', url);
    root.scrollIntoView({ block: 'start' });
    results.querySelector('h1')?.focus({ preventScroll: true });
  }

  function showLanding() {
    results.hidden = true;
    results.innerHTML = '';
    landing.hidden = false;
    const url = new URL(window.location.href);
    url.searchParams.delete('result');
    history.replaceState(null, '', url);
    updateCtas();
    root.scrollIntoView({ block: 'start' });
  }

  /* ---------------------------------------------------------------- Klaviyo */
  async function sendToKlaviyo(r) {
    const k = cfg.klaviyo;
    if (!k?.key || !state.email) return;
    const main = r.main ? pick(r.main) : {};
    const props = {
      hq_quiz: D.quiz,
      hq_brand: D.brand || null,
      hq_result: r.main || 'browse',
      hq_priority: state.answers.priority || 'skip',
      hq_focus_areas: r.main ? [r.main, ...r.also] : [],
      hq_strengths: r.strengths,
      hq_supplements: r.supplements,
      hq_recommended_hero: main.hero?.handle || null,
      hq_recommended_bundle: main.bundle?.handle || null,
      hq_completed_at: state.completedAt,
      hq_result_url: `${cfg.page}?result=${r.main || 'browse'}`
    };
    const answers = Object.fromEntries(QS.map((q) => [q.id, state.answers[q.id] || 'skip']));
    const source = new URL(window.location.href).searchParams.get('utm_campaign') || document.referrer || 'direct';
    const endpoint = (path) => `https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(k.key)}`;
    const headers = { 'Content-Type': 'application/json', revision: '2024-10-15' };

    try { window.klaviyo?.identify?.({ email: state.email, first_name: state.name }); } catch (error) { /* onsite JS optional */ }

    const calls = [
      fetch(endpoint('events'), {
        method: 'POST', headers, keepalive: true,
        body: JSON.stringify({ data: { type: 'event', attributes: {
          metric: { data: { type: 'metric', attributes: { name: k.metric } } },
          profile: { data: { type: 'profile', attributes: { email: state.email, first_name: state.name, properties: props } } },
          properties: { ...props, hq_answers: answers, hq_source: source },
          unique_id: `${D.quiz}:${state.email}:${state.completedAt}`
        } } })
      })
    ];
    if (state.consent && k.list) {
      calls.push(fetch(endpoint('subscriptions'), {
        method: 'POST', headers, keepalive: true,
        body: JSON.stringify({ data: { type: 'subscription', attributes: {
          custom_source: k.source,
          profile: { data: { type: 'profile', attributes: { email: state.email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
        }, relationships: { list: { data: { type: 'list', id: k.list } } } } })
      }));
    }
    const settled = await Promise.allSettled(calls);
    for (const s of settled) {
      if (s.status === 'rejected' || (s.value && !s.value.ok)) console.warn('[health-quiz] Klaviyo call failed', s.reason || s.value?.status);
    }
  }

  /* ------------------------------------------------------------ add to cart */
  async function addToCart(button) {
    const card = button.closest('.hq-card');
    const errBox = card?.querySelector('[data-hq-atc-err]');
    const label = button.innerHTML;
    if (errBox) errBox.hidden = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = T.r_adding;
    try {
      const body = new FormData();
      body.append('id', button.dataset.hqAdd);
      body.append('quantity', '1');
      const response = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', body, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || item.message || T.r_error);
      track('add_to_cart', {
        currency: cfg.currency,
        value: item.final_line_price / 100,
        items: [{ item_id: item.sku || item.variant_id, item_name: item.product_title, item_category: 'Health quiz', price: item.price / 100, quantity: item.quantity }]
      });
      if (typeof window.fbq === 'function') window.fbq('track', 'AddToCart', { content_ids: [item.product_id], content_type: 'product', value: item.final_line_price / 100, currency: cfg.currency });
      const cart = await (await fetch(`${shopRoot}cart.js`)).json();
      if (typeof window.emit !== 'function') {
        window.location.href = `${shopRoot}cart`;
        return;
      }
      window.emit('cart:updated', { cart });
      window.emit('quick-cart:updated');
      window.emit('quick-cart:open');
      button.removeAttribute('aria-busy');
      button.classList.add('hq-btn--done');
      button.innerHTML = `${icon('check')}${esc(T.r_added)}`;
      setTimeout(() => { button.classList.remove('hq-btn--done'); button.innerHTML = label; }, 2200);
    } catch (error) {
      button.removeAttribute('aria-busy');
      button.innerHTML = label;
      if (errBox) { errBox.textContent = error.message || T.r_error; errBox.hidden = false; }
    }
  }

  let toastTimer;
  function toast(message) {
    if (!toastEl) return;
    toastEl.textContent = message;
    toastEl.classList.add('hq-toast--show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('hq-toast--show'), 3200);
  }

  /* ----------------------------------------------------------------- events */
  root.addEventListener('click', (event) => {
    const t = event.target.closest('button, [data-hq-goto]');
    if (!t || !root.contains(t)) return;
    if (t.matches('[data-hq-start]')) return openQuiz(false);
    if (t.matches('[data-hq-restart]')) return openQuiz(true);
    if (t.matches('.hq-opt')) return choose(t.dataset.hqVal, t);
    if (t.matches('.hq-skip')) return choose('skip', null);
    if (t.matches('[data-hq-back]')) {
      if (state.step > 0 && !advancing) { state.step -= 1; save(); renderStep(true); }
      return undefined;
    }
    if (t.matches('[data-hq-close]')) return closeQuiz();
    if (t.matches('[data-hq-no-email]')) return submitCapture(false);
    if (t.matches('[data-hq-add]')) return addToCart(t);
    if (t.matches('[data-hq-goto]')) return showResults(t.dataset.hqGoto);
    if (t.matches('[data-hq-retake]')) { showLanding(); return openQuiz(true); }
    if (t.matches('[data-hq-home]')) return showLanding();
    return undefined;
  });

  root.addEventListener('submit', (event) => {
    if (!event.target.matches('[data-hq-capture]')) return;
    event.preventDefault();
    submitCapture(true);
  });

  dialog.addEventListener('keydown', (event) => {
    if (state.step >= QS.length || event.metaKey || event.ctrlKey || event.altKey || event.target.matches('input')) return;
    const key = event.key.toUpperCase();
    const q = QS[state.step];
    const idx = LETTERS.indexOf(key);
    if (key.length !== 1 || idx < 0) return;
    if (idx < q.options.length) {
      event.preventDefault();
      choose(q.options[idx][1], dialog.querySelectorAll('.hq-opt')[idx]);
    } else if (idx === q.options.length && !q.noSkip) {
      event.preventDefault();
      choose('skip', null);
    }
  });

  /* ------------------------------------------------------------------- boot */
  const wanted = new URL(window.location.href).searchParams.get('result');
  if (wanted && (AREA[wanted] || wanted === 'browse') && (state.step >= TOTAL || AREA[wanted])) {
    // Links from the results email open the result directly, even without saved answers.
    if (!state.completedAt) state.completedAt = new Date().toISOString();
    showResults(AREA[wanted] ? wanted : undefined);
  } else {
    updateCtas();
  }
  if (!AREAS.length) toast(T.empty);
})();
